import React, { lazy } from 'react';
import { motion, useTransform, useMotionValue, MotionValue } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
type Task = {
  id: string;
  tool: string;
  time: string;
  title: string;
  desc: string;
  color: string;
};
// Giga's answer to "give me a summary of what you've done in the last 24
// hours" — a focused recap of real work Giga handled across the tools the
// business already runs on, so the feed reads like a capable operator's
// end-of-day report.
const TASKS: Task[] = [{
  id: 'linkedin',
  tool: 'linkedin',
  time: '2 hours ago',
  title: 'Paused Q3 Retargeting campaign',
  desc: 'Spend was pacing 20% over budget. Alerted the growth team in Slack.',
  color: '#0A66C2'
}, {
  id: 'salesforce',
  tool: 'salesforce',
  time: '4 hours ago',
  title: 'Updated 14 deals in the pipeline',
  desc: "Logged this week's calls and moved three opportunities to closed-won.",
  color: '#00A1E0'
}, {
  id: 'stripe',
  tool: 'stripe',
  time: '6 hours ago',
  title: 'Refreshed the board deck',
  desc: 'Pulled the latest Stripe revenue numbers and updated the Q3 slides.',
  color: '#635BFF'
}, {
  id: 'gmail',
  tool: 'gmail',
  time: '9 hours ago',
  title: 'Cleared your inbox',
  desc: 'Drafted replies to 9 client threads and flagged 2 that need your call.',
  color: '#EA4335'
}, {
  id: 'googlecalendar',
  tool: 'googlecalendar',
  time: '12 hours ago',
  title: "Reorganized tomorrow's schedule",
  desc: 'Moved three meetings to open a focus block ahead of the board review.',
  color: '#4285F4'
}];
// A single timeline row. Hooks are always called unconditionally here, so the
// hook count is stable no matter whether scrollProgress is provided — fixing
// the Rules-of-Hooks issue of mapping useTransform inside the parent. When no
// scrollProgress is given (reduced motion), we feed a constant 1 so the row
// resolves fully visible.
function TaskRow({
  task,
  index,
  scrollProgress
}: {
  task: Task;
  index: number;
  scrollProgress?: MotionValue<number>;
}) {
  const fallback = useMotionValue(1);
  const source = scrollProgress ?? fallback;
  // The panel resolves around 0.82–0.84; stagger each task in just after.
  const start = 0.85 + index * 0.02;
  const end = start + 0.03;
  const opacity = useTransform(source, [start, end], [0, 1]);
  const y = useTransform(source, [start, end], [20, 0]);
  return <motion.li style={{
    opacity,
    y
  }} className="flex gap-4 sm:gap-5 relative" data-id="element-5713" data-mp="2i39nkovjn6uy">
      {/* Tool icon + completed check */}
      <div className="relative z-10 flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white border border-neutral-200 shadow-sm shrink-0" data-id="element-5714" data-mp="2mu9l9wchlsyi">
        <img src={`https://cdn.simpleicons.org/${task.tool}/${task.color.replace('#', '')}`} alt="" aria-hidden="true" className="w-5 h-5 object-contain" loading="lazy" onError={e => {
        const img = e.currentTarget as HTMLImageElement;
        if (!img.dataset.fallback) {
          img.dataset.fallback = '1';
          img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${task.tool}.svg`;
        } else {
          img.style.visibility = 'hidden';
        }
      }} data-id="element-5715" data-mp="2d46gyzzx0skv" />
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm border border-neutral-100" data-id="element-5716" data-mp="1tienc8r81tla">
          <CheckCircle size={12} className="text-[#EA580C]" data-id="element-5717" data-mp="1pg077wext7xp" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5" data-id="element-5718" data-mp="23ezuazmsxerr">
        <div className="flex items-baseline justify-between gap-2 mb-1" data-id="element-5719" data-mp="1yhr7umumeokp">
          <h4 className="text-sm sm:text-base font-bold text-[#2B211A] truncate" data-id="element-5720" data-mp="1yfddkg7rlge7">
            {task.title}
          </h4>
          <span className="text-[11px] sm:text-xs font-medium text-neutral-400 shrink-0" data-id="element-5721" data-mp="287j7hvhku7w1">
            {task.time}
          </span>
        </div>
        <p className="text-xs sm:text-sm text-neutral-500 leading-relaxed" data-id="element-5722" data-mp="2m7tzx2yjbujb">
          {task.desc}
        </p>
      </div>
    </motion.li>;
}
export function TaskFeedPanel({
  scrollProgress
}: {
  scrollProgress?: MotionValue<number>;
}) {
  return <div className="w-full max-w-[640px] mx-auto" data-id="element-5723" data-mp="khwihs8ei75g">
      <div className="relative bg-white rounded-2xl overflow-hidden border border-neutral-200 flex flex-col" style={{
      boxShadow: '0 40px 90px -25px rgba(43,33,26,0.45)',
      height: '468px'
    }} data-id="element-5724" data-mp="kgldxq74svda">
        {/* Header */}
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between bg-white z-10" data-id="element-5725" data-mp="tcatvjpt8xja">
          <div className="flex items-center gap-2" data-id="element-5726" data-mp="3sro82sf4x7x2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#EA580C]" data-id="element-5727" data-mp="1jtn8vp9fdz7" />
            <span className="font-bold text-[#2B211A] text-sm sm:text-base" data-id="element-5728" data-mp="5k4638y5hyvh">
              Giga Activity Log
            </span>
          </div>
          <span className="text-xs font-medium text-neutral-400 bg-neutral-100 px-2 py-1 rounded-md" data-id="element-5729" data-mp="al9y14jzli3y">
            Last 24 hours
          </span>
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 bg-[#FAF8F4] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden relative" data-id="element-5730" data-mp="1lxu7g7xvy91f">
          <div className="absolute left-[39px] sm:left-[43px] top-6 bottom-6 w-px bg-neutral-200" data-id="element-5731" data-mp="1w4wu2yqfm1me" />

          <ul className="flex flex-col gap-6 sm:gap-8 relative m-0 p-0 list-none" data-id="element-5732" data-mp="1r5sm2bf8u9xn">
            {TASKS.map((task, i) => <TaskRow key={task.id} task={task} index={i} scrollProgress={scrollProgress} data-id="element-5733" data-mp="1mamscqflnqz0" data-mpi={i} />)}
          </ul>
        </div>
      </div>
    </div>;
}